import { applyPatchToTextNode } from "./applyRuntimePatches.js";
import { collectBehaviorDependencies } from "./compileEvents.js";
import { compileBehavior } from "./compileBehavior.js";
import {
  dependencyFromDataBindingPath,
  evaluateWithRenderCache,
} from "./renderCache.js";
import {
  applyRuntimeDomTargetFlags,
  runCompiledNodeBehavior,
  withCurrentNodeGeometry,
} from "./applyNodeBehavior.js";
import type {
  CompiledTextNode,
  CompileNodeContext,
  RenderTextNode,
  RuntimeContext,
} from "./types.js";
import { PrimitiveNode } from "../layout/types.js";
import {
  compileNodeStyle,
  resolvePrimitiveTooltip,
  transitionsTouchTopLeft,
} from "./helpers.js";
import { formatBindingPathDisplayValue } from "../position-rack.js";
import { resolvePath, uniqueDependencies } from "./method.js";

function resolveDataBinding(path: string, ctx: RuntimeContext) {
  const normalizedPath = dependencyFromDataBindingPath(path);
  return resolvePath(ctx, normalizedPath);
}

export function compileTextNode(
  node: PrimitiveNode,
  compileContext: CompileNodeContext,
): CompiledTextNode {
  if (node.kind !== "primitive" || node.primitive !== "text") {
    throw new Error("Node is not a text primitive");
  }

  const baseStyle = compileNodeStyle(node.style, node.transitions);
  const fallbackText = node.text ?? "";
  const behavior = node.behavior
    ? compileBehavior(
        node.behavior,
        node.id,
        compileContext.catalogDeps,
        undefined,
        compileContext.layoutNodeById,
      )
    : undefined;

  const dependencies = uniqueDependencies([
    ...(behavior ? collectBehaviorDependencies(behavior, compileContext) : []),
    ...(node.tooltip?.kind === "layout"
      ? ["layoutListRowItem", "rowIndex"]
      : []),
    ...(node.valueKind === "binding" && node.dataBinding?.path
      ? [dependencyFromDataBindingPath(node.dataBinding.path)]
      : []),
  ]);

  return {
    id: node.id,
    dependencies,

    evaluate(ctx: RuntimeContext): RenderTextNode | null {
      return evaluateWithRenderCache(ctx, node.id, dependencies, () => {
        const style = { ...baseStyle };

        // Localize rackPosition/positionRack labels at display time (storage stays FR-canonical)
        const text =
          node.valueKind === "binding" && node.dataBinding?.path
            ? formatBindingPathDisplayValue(
                node.dataBinding.path,
                resolveDataBinding(node.dataBinding.path, ctx),
              )
            : fallbackText;

        const tooltip = resolvePrimitiveTooltip(node.tooltip, ctx);

        let renderNode: RenderTextNode = {
          id: node.id,
          key: ctx.__nodeInstanceKeyPrefix
            ? `${ctx.__nodeInstanceKeyPrefix}${node.id}`
            : node.id,
          x: node.x,
          y: node.y,
          width: node.width,
          height: node.height,
          zIndex: node.zIndex,
          rotation: node.rotation ?? 0,
          kind: "text",
          preserveBoundText: node.valueKind === "binding",
          text,
          textWidthMode: node.textWidthMode,
          style,
          hasTopLeftTransition: transitionsTouchTopLeft(node.transitions),
          ...(tooltip ? { tooltip } : {}),
        };

        if (behavior) {
          const behaviorCtx = withCurrentNodeGeometry(ctx, {
            x: node.x,
            y: node.y,
            width: node.width,
            height: node.height,
            rotation: node.rotation ?? 0,
          });
          const { patches, effects, continuations, runtimeDomTarget } =
            runCompiledNodeBehavior(behavior, behaviorCtx, node.id);

          for (const patch of patches) {
            renderNode = applyPatchToTextNode(renderNode, patch);
          }

          ctx.__runtimeEffects?.push(...effects);
          ctx.__runtimeContinuations?.push(...continuations);
          renderNode = applyRuntimeDomTargetFlags(
            renderNode,
            runtimeDomTarget,
            behavior,
          );
        }

        if (renderNode.style.visibility === false) {
          return null;
        }

        return renderNode;
      });
    },
  };
}
